import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../data/site";

const LINES = [
    { text: "FORGED IN FIRE.", accent: false },
    { text: "MACHINED FOR", accent: false },
    { text: "PRECISION.", accent: true },
];

const META = ["Chennai — India", "Forging / Casting / Machining", "OEM & Export"];

export const Hero = ({ ready, lenis }) => {
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 900], [0, 240]);
    const opacity = useTransform(scrollY, [0, 650], [1, 0.2]);

    const go = (href) => {
        if (lenis) lenis.scrollTo(href, { offset: -64, duration: 1.4 });
        else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col overflow-hidden"
        >
            <motion.div style={{ y }} className="absolute inset-0 scale-110">
                <img
                    src={IMAGES.heroForge}
                    alt="Glowing steel being forged with sparks"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/65" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-black/70" />
            </motion.div>

            <motion.div
                style={{ opacity }}
                className="relative z-10 mx-auto flex w-full max-w-screen-2xl flex-1 flex-col justify-end px-6 pb-14 pt-32 md:px-12 md:pb-20"
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={ready ? { opacity: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.9 }}
                    className="mb-8 flex flex-wrap gap-x-8 gap-y-2"
                >
                    {META.map((m) => (
                        <span
                            key={m}
                            className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 md:text-xs"
                        >
                            {m}
                        </span>
                    ))}
                </motion.div>

                <h1 className="font-display uppercase leading-[0.92] tracking-tight text-zinc-100">
                    {LINES.map((line, i) => (
                        <span key={line.text} className="block overflow-hidden pb-1">
                            <motion.span
                                initial={{ y: "115%" }}
                                animate={ready ? { y: "0%" } : {}}
                                transition={{
                                    duration: 1.1,
                                    delay: 0.15 + i * 0.13,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className={`block text-[13vw] md:text-[9.5vw] lg:text-[8.5vw] ${
                                    line.accent ? "text-ember" : ""
                                }`}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={ready ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-cta-capabilities"
                        onClick={() => go("#capabilities")}
                        className="bg-ember px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-ember-hover"
                    >
                        Explore Capabilities
                    </button>
                    <button
                        data-testid="hero-cta-quote"
                        onClick={() => go("#contact")}
                        className="border border-zinc-600 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-200 transition-colors hover:border-ember hover:text-ember"
                    >
                        Request a Quote
                    </button>
                    <div className="ml-auto hidden items-center gap-3 md:flex">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                            Scroll
                        </span>
                        <motion.span
                            animate={{ y: [0, 6, 0] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <ArrowDown size={14} className="text-ember" />
                        </motion.span>
                    </div>
                </motion.div>
            </motion.div>
        </section>
    );
};
